import React, { useMemo, useState } from 'react';
import useTranslation from '~/app/hooks/useTranslation';
import Icon from '~/app/common/Icon';
import {
    useCostCategories,
    useCustomers,
    useProjects,
    useWorkTypes,
} from '~/app/data/statuses/issue';
import { useStoreState } from '~/store';
import { WorkOrderDetail } from '~/workorder/types';
import { resolveOptionLabel } from '~/workorder/helper';
import classes from './style.module.scss';

export type AdministrativeComponent = React.FC<{ data: WorkOrderDetail }>;

const Administrative: AdministrativeComponent = ({ data }) => {
    const { t } = useTranslation('workorder');
    const [expanded, setExpanded] = useState(false);

    const workTypes = useStoreState(state => state.issue.workTypes);
    const costCategories = useStoreState(state => state.issue.costCategories);
    const customers = useStoreState(state => state.issue.customers);
    const projects = useStoreState(state => state.projects.list);
    const departments = useStoreState(state => state.app.appData.departments);

    const workTypeOptions = useWorkTypes(workTypes);
    const costCategoryOptions = useCostCategories(costCategories);
    const customerOptions = useCustomers(customers);
    const projectOptions = useProjects(projects);
    const sortingTypeOptions = useMemo(() => [
        {text: t('detail.sortingTypeOptions.0'), value: 0},
        {text: t('detail.sortingTypeOptions.1'), value: 1},
    ], [t]);

    const departmentOptions = useMemo(
        () => departments?.map(dept => ({
            text: dept.name,
            value: dept.id,
        })) ?? [],
        [departments],
    );

    const employees = useMemo(
        () => data.mechanics?.map(m => m.name).filter(Boolean).join(', ') || null,
        [data.mechanics],
    );

    const rows = useMemo(() => [
        {
            label: t('detail.department'),
            value: resolveOptionLabel(departmentOptions, data.departmentId),
        },
        { label: t('detail.createdBy'), value: data.createdBy?.name },
        { label: t('detail.approvedBy'), value: data.approvedBy?.name },
        { label: t('detail.responsible'), value: data.personnel?.name },
        { label: t('detail.employees'), value: employees },
        { label: t('detail.workshop'), value: data.repairLocation?.trim() || null },
        {
            label: t('detail.workType'),
            value: resolveOptionLabel(workTypeOptions, data.workType),
        },
        {
            label: t('detail.costCategory'),
            value: resolveOptionLabel(costCategoryOptions, data.costCategoryId),
        },
        {
            label: t('detail.chargedDepartment'),
            value: resolveOptionLabel(departmentOptions, data.chargedDepartmentId),
        },
        {
            label: t('detail.customer'),
            value: resolveOptionLabel(customerOptions, data.customerId),
        },
        {
            label: t('detail.project'),
            value: resolveOptionLabel(projectOptions, data.projectId),
        },
        {
            label: t('detail.sortingType'),
            value: resolveOptionLabel(sortingTypeOptions, data.sortingType),
        },
        {
            label: t('detail.approvalInvoice'),
            value: data.invoiceAcceptanceUser,
        }
    ], [
        t,
        data,
        employees,
        departmentOptions,
        workTypeOptions,
        costCategoryOptions,
        customerOptions,
        projectOptions,
        sortingTypeOptions,
    ]);

    return (
        <div className={classes['card']}>
            <div className={classes['header']} onClick={() => setExpanded(prev => !prev)}>
                <h2 className={classes['title']}>{t('detail.administrativeInfo')}</h2>
                <Icon name={expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} className={classes['chevron']} />
            </div>

            {expanded && (
                <>
                    <div className={classes['rows']}>
                        {rows.map(row => (
                            <div key={row.label} className={classes['row']}>
                                <span className={classes['label']}>{row.label}</span>
                                <span className={classes['value']}>{row.value?.trim() ?? '-'}</span>
                            </div>
                        ))}
                    </div>
                    {false && <button type="button" className={classes['edit-btn']}>
                        <Icon name="edit" size={18} />
                        {t('detail.edit')}
                    </button>}
                </>
            )}
        </div>
    );
};

export default Administrative;
